import { useCallback } from 'react';
import { AlignedData, Options } from 'uplot';
import Chart from '../../../graph/LineGraph';
import { ChartTooltipSingle } from '../../../graph/ChartTooltipSingle';
import { TooltipData } from '../../../graph/types';
import { ViewersStatsForChart } from './transform-viewers-stats-for-uplot';
import { YoutubeStatsSingleTooltip } from './YoutubeStatsSingleTooltip';
import {
  buildYoutubeWatchUrlAtOffset,
  canShowYoutubeWatchActionForVideo,
  computeVideoOffsetSeconds,
  findVideoForChannel,
} from './youtube-stats-video-utils';

type YoutubeStatsLineChartProps = {
  options: Options;
  data: AlignedData;
  stats: ViewersStatsForChart | undefined;
  seriesChannelIds: string[];
  timezone: string;
};

/**
 * YouTube stats v2 chart with single-line tooltip text and click-through to YouTube at the hovered offset.
 */
export function YoutubeStatsLineChart(props: YoutubeStatsLineChartProps) {
  const { options, data, stats, seriesChannelIds, timezone } = props;

  const resolveYoutubeContext = useCallback(
    (tooltipData: TooltipData) => {
      if (tooltipData.mode !== 'single' || tooltipData.timestampUnix == null) {
        return null;
      }

      const uPlotSeriesIndex = tooltipData.series[0]?.uPlotSeriesIndex ?? tooltipData.seriesIndex;
      if (uPlotSeriesIndex == null) {
        return null;
      }

      const channelId = seriesChannelIds[uPlotSeriesIndex - 1];
      if (!channelId) {
        return null;
      }

      const video = findVideoForChannel(
        stats?.metadata?.videos,
        channelId,
        tooltipData.timestampUnix,
        timezone,
      );
      if (!video?.startedAt || !canShowYoutubeWatchActionForVideo(video, timezone)) {
        return null;
      }

      const offsetSeconds = computeVideoOffsetSeconds(tooltipData.timestampUnix, video, timezone);

      return {
        video,
        offsetSeconds,
      };
    },
    [seriesChannelIds, stats?.metadata?.videos, timezone],
  );

  const renderSingleTooltip = useCallback(
    (tooltipData: TooltipData) => {
      const youtubeContext = resolveYoutubeContext(tooltipData);
      if (!youtubeContext) {
        return <ChartTooltipSingle data={tooltipData} />;
      }

      return <YoutubeStatsSingleTooltip data={tooltipData} />;
    },
    [resolveYoutubeContext],
  );

  const onPointClick = useCallback(
    (tooltipData: TooltipData) => {
      const youtubeContext = resolveYoutubeContext(tooltipData);
      if (!youtubeContext) {
        return;
      }

      const url = buildYoutubeWatchUrlAtOffset(
        youtubeContext.video.id,
        youtubeContext.offsetSeconds,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [resolveYoutubeContext],
  );

  const pointerOnSingleLineHover = Boolean(
    stats?.metadata?.videos && Object.keys(stats.metadata.videos).length > 0,
  );

  return (
    <Chart
      options={options}
      data={data}
      width=""
      height=""
      timezone={timezone}
      tooltipTimestampFormat="HH:mm"
      pointerOnSingleLineHover={pointerOnSingleLineHover}
      renderSingleTooltip={renderSingleTooltip}
      onPointClick={onPointClick}
    />
  );
}
